import Dialog from '@jetbrains/ring-ui-built/components/dialog/dialog'
import {Content, Header} from '@jetbrains/ring-ui-built/components/island/island'
import Link from '@jetbrains/ring-ui-built/components/link/link'
import Panel from '@jetbrains/ring-ui-built/components/panel/panel'
import {Button, IconButton} from '@jetbrains/teamcity-ui/dist/components'
import {trackEvent} from '@jetbrains/teamcity-ui/dist/statistics'
import {PlaceId} from '@jetbrains/teamcity-ui/plugin'
import {Fragment, useEffect} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {useAppSelector} from '../../../../hooks/react-redux'
import {useUserProperty} from '../../../../hooks/useUserProperty'
import {getServerInfo} from '../../../../selectors'
import {Permission} from '../../../../types'
import {isCloud, isWelcomePopupEnabled} from '../../../../utils/featureToggles'
import {UserProperties} from '../../../../utils/userProperties'
import usePluginWrapper from '../../../common/Plugin/PluginWrapper/usePluginWrapper'
import welcomePopupAiAssistant from '../assets/welcomePopupAiAssistant.png'
import styles from '../WelcomePopup.module.css'
import type {WelcomePopupProps} from '../WelcomePopup.type'

import type {WelcomePopupAiAssistant202511Query} from './__generated__/WelcomePopupAiAssistant202511Query.graphql'
import {
  adminSettingLink,
  callToActionLabel,
  callToActionLink,
  subText,
  subtitle,
  text,
  title,
} from './WelcomePopupAiAssistant202511.const'

const query = graphql`
  query WelcomePopupAiAssistant202511Query($locator: String!, $skip: Boolean!) {
    userPermissions(userLocator: "current", locator: $locator) @skip(if: $skip) {
      count
    }
  }
`
function WelcomePopupAiAssistant202511({
  isShowPopup,
  onClose,
  onCallToActionClick,
}: WelcomePopupProps) {
  const buildNumber = useAppSelector(state => getServerInfo(state)?.buildNumber)
  const {hasPlugins} = usePluginWrapper(PlaceId.SAKURA_AI_ASSISTANT_CHAT)
  const userServerBuildNumber = useUserProperty(UserProperties.SERVER_BUILD_NUMBER)
  const isOldUser =
    userServerBuildNumber && buildNumber && Number(userServerBuildNumber) < Number(buildNumber)
  const isSkipPermissionQuery =
    !isShowPopup || !isWelcomePopupEnabled() || !hasPlugins || !isOldUser
  const {userPermissions} = useLazyLoadQuery<WelcomePopupAiAssistant202511Query>(query, {
    locator: `permission:${Permission.CHANGE_SERVER_SETTINGS},count:1`,
    skip: isSkipPermissionQuery,
  })
  const isUserAdmin = userPermissions?.count
  const canBeShown = isWelcomePopupEnabled() && hasPlugins && isOldUser && !isCloud

  useEffect(() => {
    if (isShowPopup && canBeShown) {
      trackEvent({name: 'welcome.popup.open', groupId: 'welcomepopup.events.group'})
    }
  }, [canBeShown, isShowPopup])

  if (!canBeShown) {
    return null
  }

  return (
    <Dialog
      closeButtonInside
      showCloseButton
      show={isShowPopup}
      label={title}
      contentClassName={styles.dialog}
      onCloseAttempt={onClose}
    >
      <Header>{title}</Header>
      <Content className={styles.content}>
        <div className={styles.subtitle}>{subtitle}</div>
        <div className={styles.text}>{text}</div>
        <div className={styles.text}>
          {isUserAdmin ? (
            <Fragment>
              {'Enable it under '}
              <Link href={adminSettingLink}>{'Admin / AI Assistant'}</Link>
              {' to give it a try.'}
            </Fragment>
          ) : (
            'Ask your administrator to enable AI Assistant.'
          )}
        </div>
        <div className={styles.subText}>{subText}</div>
        <div className={styles.imageContainer}>
          <img
            src={welcomePopupAiAssistant}
            alt="TeamCity AI Assistant is your 24/7 companion"
            className={styles.image}
          />
        </div>
      </Content>
      <Panel className={styles.pager}>
        <IconButton
          primary
          withBorder
          className={styles.link}
          icon="new-window"
          href={callToActionLink}
          target="_blank"
          rel="noreferrer"
          title={callToActionLabel}
          onClick={onCallToActionClick}
        >
          {'Learn more'}
        </IconButton>
        <Button onClick={onClose}>{'Dismiss'}</Button>
      </Panel>
    </Dialog>
  )
}

export default WelcomePopupAiAssistant202511
